import React, { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AiOutlineUserAdd,
  AiOutlineSearch,
  AiOutlineExclamationCircle
} from "react-icons/ai";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaUserShield, FaUser, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import UserManagementService from "../../services/UserManagementService";
import PrintableTable from "../../components/PrintableTable/PrintableTable";
import Loader from "../../components/Loader/Loader";
import UserDetailsModal from "../../modals/UserDetailsModal/UserDetailsModal";

import "./UsersList.css";
import "./UsersModal.css";
import "../../styles/action-icons.css";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [selectedUser, setSelectedUser] = useState({});
  const [selectedUserForDetails, setSelectedUserForDetails] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await UserManagementService.getAll();
      console.log('Users API response:', res.data);
      // Handle different response formats
      const list = Array.isArray(res.data) ? res.data : (res.data.users || []);
      setUsers(list);
      setFilteredUsers(list);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      toast.error(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filter logic
  useEffect(() => {
    if (!Array.isArray(users)) return;
    
    const lower = searchTerm.toLowerCase();
    setFilteredUsers(
      users.filter(u =>
        String(u.user_id || u.ID).includes(lower) ||
        (u.firstName || "").toLowerCase().includes(lower) ||
        (u.lastName || "").toLowerCase().includes(lower) ||
        (u.userName || "").toLowerCase().includes(lower) ||
        (u.email || "").toLowerCase().includes(lower) ||
        (u.role || "").toLowerCase().includes(lower)
      )
    );
  }, [searchTerm, users]);

  // Selection handlers
  const handleSelectOne = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u.user_id));
    }
  };

  const handleDelete = async () => {
    try {
      await UserManagementService.remove(selectedUserId);
      toast.success("User deleted successfully");
      loadUsers();
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(err.response?.data?.message || "Failed to delete user");
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedUserIds.map((id) => UserManagementService.remove(id)));
      toast.success(`${selectedUserIds.length} user(s) deleted successfully`);
      setSelectedUserIds([]);
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete selected users");
    }
  };

  const deleteClickHandler = (e, id) => {
    e.stopPropagation();
    setSelectedUserId(id);
    setShowDeleteModal(true);
  };

  const editClickHandler = (e, user) => {
    e.stopPropagation();
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // For export/save, pass only selected users if any, else all
  const getUserComparableValue = (user, key) => {
    switch (key) {
      case 'id':
        return Number(user.user_id || user.ID) || 0;
      case 'firstName':
        return (user.firstName || "").toLowerCase();
      case 'lastName':
        return (user.lastName || "").toLowerCase();
      case 'username':
        return (user.userName || "").toLowerCase();
      case 'email':
        return (user.email || "").toLowerCase();
      case 'phone':
        return (user.phoneNumber || "").toLowerCase();
      case 'role':
        return (user.role || "").toLowerCase();
      default:
        return '';
    }
  };

  const sortedUsers = useMemo(() => {
    const data = [...filteredUsers];
    if (!sortConfig?.key) return data;
    data.sort((a, b) => {
      const aVal = getUserComparableValue(a, sortConfig.key);
      const bVal = getUserComparableValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredUsers, sortConfig]);

  const usersToDisplay = selectedUserIds.length > 0
    ? sortedUsers.filter(u => selectedUserIds.includes(u.user_id))
    : sortedUsers;

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" aria-hidden="true" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" aria-hidden="true" />
      : <FaSortDown className="sort-icon" aria-hidden="true" />;
  };

  if (loading) {
    return <Loader message="Loading users..." />;
  }

  return (
    <>
      <div className="users-list">
        <div className="users-title">Users Management</div>

        <div className="users-header row">
          <div className="col add-btn-col">
            <button
              className="btn add-user-button"
              onClick={() => setShowAddModal(true)}
            >
              <AiOutlineUserAdd /> Add User
            </button>
          </div>
          <div className="col search-bar-col">
            <div className="search-bar-wrapper">
              <span className="search-icon">
                <AiOutlineSearch />
              </span>
              <input
                type="text"
                placeholder="Search Users..."
                className="search-bar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {selectedUserIds.length > 0 && (
          <div className="selection-actions">
            <span>{selectedUserIds.length} user(s) selected</span>
            <button
              className="btn btn-danger delete-selected-button"
              onClick={handleDeleteSelected}
            >
              <FiTrash2 /> Delete Selected
            </button>
          </div>
        )}

        <div className="table-responsive">
          <PrintableTable 
            fileName="users.pdf"
            title="Users List"
            showOptions={true}
            data={usersToDisplay}
          >
            <table className="table users-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('id')}>
                      ID {renderSortIcon('id')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('firstName')}>
                      First Name {renderSortIcon('firstName')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('lastName')}>
                      Last Name {renderSortIcon('lastName')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('username')}>
                      Username {renderSortIcon('username')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('email')}>
                      Email {renderSortIcon('email')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('phone')}>
                      Phone {renderSortIcon('phone')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('role')}>
                      Role {renderSortIcon('role')}
                    </button>
                  </th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {usersToDisplay.length > 0 ? (
                  usersToDisplay.map((user) => {
                    const id = user.user_id;
                    return (
                      <tr 
                        key={id} 
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          // Don't open modal if clicking on action buttons or checkboxes
                          if (e.target.closest('.no-click') || e.target.type === 'checkbox') {
                            return;
                          }
                          setSelectedUserForDetails(user);
                          setShowDetailsModal(true);
                        }}
                      >
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(id)}
                            onChange={() => handleSelectOne(id)}
                          />
                        </td>
                        <td className="text-center">{id}</td>
                        <td className="text-center">{user.firstName}</td>
                        <td className="text-center">{user.lastName}</td>
                        <td className="text-center">{user.userName}</td>
                        <td className="text-center">{user.email}</td>
                        <td className="text-center">{user.phoneNumber}</td>
                        <td className="text-center">
                          <span className={`role-badge role-${user.role}`}>
                            {user.role === 'admin' ? (
                              <>
                                <FaUserShield /> Admin
                              </>
                            ) : (
                              <>
                                <FaUser /> User
                              </>
                            )}
                          </span>
                        </td>
                        <td className="text-center no-print no-click">
                          <div className="table-action-group">
                            <button
                              type="button"
                              className="table-icon-button edit-button no-click"
                              onClick={(e) => editClickHandler(e, user)}
                              title="Edit user"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="table-icon-button delete-button no-click"
                              onClick={(e) => deleteClickHandler(e, id)}
                              title="Delete user"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </PrintableTable>
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        showModal={showAddModal}
        handleClose={() => setShowAddModal(false)}
        onUserAdded={loadUsers}
      />

      {/* Edit User Modal */}
      <EditUserModal
        showModal={showEditModal}
        handleClose={() => setShowEditModal(false)}
        userData={selectedUser}
        onUserUpdated={loadUsers}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        user={selectedUserForDetails}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AiOutlineExclamationCircle className="warning-icon" />
          <p>Are you sure you want to delete this user?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

// Add User Modal Component
const AddUserModal = ({ showModal, handleClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    phoneNumber: "",
    dob: "",
    gender: "",
    password: "",
    role: "user",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await UserManagementService.create(formData);
      toast.success("User added successfully");
      handleClose();
      onUserAdded();
      setFormData({
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        phoneNumber: "",
        dob: "",
        gender: "",
        password: "",
        role: "user",
      });
    } catch (err) {
      console.error("Failed to add user:", err);
      toast.error(err.response?.data?.message || "Failed to add user");
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title className="title">Add New User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              className="form-control"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              className="form-control"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              className="form-control"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              className="form-control"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              className="form-control"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Gender</label>
            <select
              className="form-control"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              className="form-control"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select
              className="form-control"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end w-100">
          <Button variant="primary" onClick={handleSubmit} className="w-100">
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Edit User Modal Component
const EditUserModal = ({ showModal, handleClose, userData, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    phoneNumber: "",
    dob: "",
    gender: "",
    role: "user",
  });

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      setFormData({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        userName: userData.userName || "",
        email: userData.email || "",
        phoneNumber: userData.phoneNumber || "",
        dob: userData.dob ? userData.dob.split('T')[0] : "",
        gender: userData.gender || "",
        role: userData.role || "user",
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const id = userData.user_id || userData.ID;
      await UserManagementService.update(id, formData);
      toast.success("User updated successfully");
      handleClose();
      onUserUpdated();
    } catch (err) {
      console.error("Failed to update user:", err);
      toast.error(err.response?.data?.message || "Failed to update user");
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title className="title">Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              className="form-control"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              className="form-control"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              className="form-control"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              required
              disabled
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              className="form-control"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              className="form-control"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Gender</label>
            <select
              className="form-control"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Role</label>
            <select
              className="form-control"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end w-100">
          <Button variant="primary" onClick={handleSubmit} className="w-100">
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UsersList;

